import { Icon, IconName, ServiceIcon } from '@zapier/design-system';
import { clsx } from 'clsx';
import { Context, useContext } from 'react';
import {
  TagProps as ReactAriaTagProps,
  Tag,
  TagGroupContext,
  useContextProps,
} from 'react-aria-components';
import { TagGroupContextValue } from './SelectionChipList';

import styles from './SelectionChip.module.scss';

export type SelectionChipProps = Omit<ReactAriaTagProps, 'children'> & {
  /** The contents of `SelectionChip`. Should be `SelectionChipIcon` or `SelectionChipServiceIcon`. A function may be provided to alter the children based on the chip's state. */
  children?: ReactAriaTagProps['children'];
  /** A label rendered next to the title.  */
  label?: string;
  /**
   * The size of the `SelectionChip`. Use only to override the size for an individual `SelectionChip`. Otherwise, pass `size` to `ChipList`.
   * @default 'small'
   */
  size?: 'compact' | 'small' | 'medium';
  /** The title of the `SelectionChip`. */
  title?: string;
  /**
   * The visual style of the `SelectionChip`. Use only to override the variant for an individual `SelectionChip`. Otherwise, pass `variant` to `SelectionChipList`.
   * @default 'primary'
   */
  variant?: 'primary' | 'neutral' | 'brand';
};

/**
 * A single chip that can be used to indicate a selection. Must be rendered inside of `SelectionChipList`.
 */
export const SelectionChip = ({
  children,
  className,
  id,
  isDisabled = false,
  label,
  style,
  textValue,
  title,
  ...otherProps
}: SelectionChipProps) => {
  const [contextProps] = useContextProps(otherProps, null, TagGroupContext);
  const { size = 'small', variant = 'primary', ...props } = contextProps;

  const tagGroupContext = useContext(
    TagGroupContext as Context<TagGroupContextValue>
  );

  const getTextValue = () => {
    if (textValue) return textValue;

    return label ? `${title} ${label}` : title;
  };

  const hasNoTitleAndNoLabel = !title && !label;

  return (
    <Tag
      {...props}
      className={clsx(className, styles.root)}
      data-has-no-title-or-label={hasNoTitleAndNoLabel ? 'true' : undefined}
      data-selectable={tagGroupContext?.selectionMode ?? undefined}
      data-size={size}
      data-variant={variant}
      data-zds
      id={id}
      isDisabled={isDisabled}
      style={style}
      textValue={getTextValue()}
    >
      {(tagRenderProps) => (
        <>
          {typeof children === 'function' ? children(tagRenderProps) : children}

          {title && (
            <div className={styles.titleLabelWrapper} data-zds>
              <span className={styles.title} data-zds>
                {title}
              </span>
              {label && (
                <span className={styles.label} data-zds>
                  {label}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </Tag>
  );
};

export type ChipServiceIconProps = {
  /** The name of the service whose icon is being displayed (used as alt text for the icon), e.g., "Google Sheets". */
  serviceName: string;
  /** A URL for the service icon image to be rendered. If this is an Imgix URL, a large-format image can be used and the image will automatically be resized. */
  src: string;
};

/** A service icon whose styles will dynamically adjust when passed as a child to `SelectionChip`. */
export const SelectionChipServiceIcon = ({
  serviceName,
  src,
}: ChipServiceIconProps) => (
  <ServiceIcon
    hasBorder={false}
    size="large"
    serviceName={serviceName}
    src={src}
  />
);

export type ChipIconProps = {
  /** The name of icon to display. If the name is invalid, a square will be rendered instead. */
  name: IconName;
};

/** An icon whose styles will dynamically adjust when passed as a child to `SelectionChip`. */
export const SelectionChipIcon = ({ name }: ChipIconProps) => (
  <Icon className={styles.selectionChipIcon} name={name} />
);
